import { useState, useEffect } from 'react';
import {
    Container,
    Paper,
    Typography,
    Box,
    Tabs,
    Tab,
    Divider
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { travelResources, travelResourcesOrder } from '../data/travelResources';

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            {...other}
        >
            {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
        </div>
    );
}

const TravelResourcesPage = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [tabValue, setTabValue] = useState(0);

    // Get tab from URL query parameter
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const tab = params.get('tab');
        if (tab !== null) {
            const tabIndex = parseInt(tab, 10);
            if (!isNaN(tabIndex) && tabIndex >= 0 && tabIndex < travelResourcesOrder.length) {
                setTabValue(tabIndex);
            }
        }
    }, [location.search]);

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
        navigate(`/travel-resources?tab=${newValue}`);
    };

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <Paper elevation={3} sx={{ p: 4 }}>
                <Typography variant="h4" gutterBottom sx={{ color: '#009688', mb: 3 }}>
                    Travel Resources
                </Typography>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Essential travel information, guidelines, and procedures for IOD PARC staff
                </Typography>

                <Divider sx={{ mb: 3 }} />

                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tabs 
                        value={tabValue} 
                        onChange={handleTabChange}
                        variant="scrollable"
                        scrollButtons="auto"
                    >
                        {travelResourcesOrder.map((key) => (
                            <Tab 
                                key={key} 
                                label={travelResources[key].title}
                            />
                        ))}
                    </Tabs>
                </Box>

                {travelResourcesOrder.map((key, index) => (
                    <TabPanel key={key} value={tabValue} index={index}>
                        <Box>
                            <Typography variant="h5" gutterBottom sx={{ color: '#009688', mb: 2 }}>
                                {travelResources[key].title}
                            </Typography>
                            
                            <Typography 
                                variant="body1" 
                                component="div"
                                sx={{ 
                                    whiteSpace: 'pre-wrap',
                                    lineHeight: 1.8,
                                    '& p': { mb: 2 },
                                    '& ul': { mb: 2, pl: 3 },
                                    '& ol': { mb: 2, pl: 3 },
                                    '& li': { mb: 1 },
                                    '& h1, & h2, & h3, & h4, & h5, & h6': { 
                                        mt: 3, 
                                        mb: 2,
                                        color: '#009688',
                                        fontWeight: 600
                                    }
                                }}
                            >
                                {travelResources[key].content}
                            </Typography>
                        </Box>
                    </TabPanel>
                ))}
            </Paper>
        </Container>
    );
};

export default TravelResourcesPage;
